import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { getBirdsEyeView, searchInstructors, updateActivitySlotStatus, bulkUpdateActivitySlotStatus } from '../../_services/schedules.service';
import type {
  BirdsEyeViewData,
  BirdsEyeViewFilters,
  ActivitySlot,
} from '../../_models/schedule.model';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import StudentListDialog from './components/StudentListDialog';
import AssetUtilizationModal from './components/AssetUtilizationModal';
import './birds-eye-view.css';

type ViewMode = 'all' | 'by-instructor' | 'by-room' | 'by-centre' | 'by-shift' | 'by-date';

const BirdsEyeView: React.FC = () => {
  // Helper function to get default date range (current month)
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: lastDayOfMonth.toISOString().split('T')[0],
    };
  };

  const [data, setData] = useState<BirdsEyeViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [filters, setFilters] = useState<BirdsEyeViewFilters>(getDefaultDateRange());
  const [showFilters, setShowFilters] = useState(true);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [searchSlotId, setSearchSlotId] = useState<string>('');

  // Bulk update states
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<'Scheduled' | 'Conducted' | 'Cancelled'>('Conducted');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Quick action states
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [updatingSlot, setUpdatingSlot] = useState<number | null>(null);
  const ignoreNextClickRef = useRef(false);

  // Confirmation dialog states
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    slotId?: number;
    slotIds?: number[];
    status: 'Scheduled' | 'Conducted' | 'Cancelled';
    isBulk: boolean;
  } | null>(null);

  // Batch slots modal states
  const [showBatchSlotsModal, setShowBatchSlotsModal] = useState(false);
  const [selectedBatchData, setSelectedBatchData] = useState<{
    batchId: number;
    batchName: string;
    slots: ActivitySlot[];
  } | null>(null);

  // Student list dialog states
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [selectedSlotForStudents, setSelectedSlotForStudents] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Utilization modal states
  const [roomUtilizationModalOpen, setRoomUtilizationModalOpen] = useState(false);
  const [instructorUtilizationModalOpen, setInstructorUtilizationModalOpen] = useState(false);

  // Router hook for navigation
  const history = useHistory();

  // Function to open TV display board in new tab
  const openTVDisplay = (centreId: number, centreName: string) => {
    const url = `/display-board/${centreId}?centreName=${encodeURIComponent(centreName)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Skip if we should ignore this click
      if (ignoreNextClickRef.current) {
        ignoreNextClickRef.current = false;
        return;
      }

      const target = e.target as HTMLElement;
      if (!target.closest('.bev-slot-actions')) {
        setOpenActionMenu(null);
      }
    };

    if (openActionMenu !== null) {
      // Add listener on next tick to avoid capturing the opening click
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [openActionMenu]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBirdsEyeView(filters);
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof BirdsEyeViewFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  // Bulk mode handlers
  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedSlots(new Set());
  };

  const toggleSlotSelection = (slotId: number) => {
    const newSelected = new Set(selectedSlots);
    if (newSelected.has(slotId)) {
      newSelected.delete(slotId);
    } else {
      newSelected.add(slotId);
    }
    setSelectedSlots(newSelected);
  };

  const selectAllSlots = () => {
    if (!data) return;
    const allSlotIds = new Set(data.slots.map(slot => slot.id));
    setSelectedSlots(allSlotIds);
  };

  const deselectAllSlots = () => {
    setSelectedSlots(new Set());
  };

  // Status update handlers
  const handleQuickStatusUpdate = (slotId: number, status: 'Scheduled' | 'Conducted' | 'Cancelled') => {
    setConfirmationData({
      slotId,
      status,
      isBulk: false,
    });
    setShowConfirmation(true);
    setOpenActionMenu(null);
  };

  const handleBulkStatusUpdate = () => {
    if (selectedSlots.size === 0) {
      alert('Please select at least one slot to update');
      return;
    }

    setConfirmationData({
      slotIds: Array.from(selectedSlots),
      status: bulkStatus,
      isBulk: true,
    });
    setShowConfirmation(true);
  };

  const confirmStatusUpdate = async () => {
    if (!confirmationData) return;

    try {
      if (confirmationData.isBulk && confirmationData.slotIds) {
        setBulkUpdating(true);
        console.log('Sending bulk update request:', {
          slotIds: confirmationData.slotIds,
          status: confirmationData.status
        });

        const response = await bulkUpdateActivitySlotStatus(
          confirmationData.slotIds,
          confirmationData.status
        );

        console.log('Bulk update response:', response);

        if (response.success) {
          alert(`✓ Successfully updated ${response.data?.successCount || 0} slots`);
          setSelectedSlots(new Set());
          setBulkMode(false);
          await loadData(); // Reload data to reflect changes
        } else {
          console.error('Bulk update failed:', response);
          alert(`✗ Failed to update slots: ${response.message || response.error || 'Unknown error'}. Please try again.`);
        }
      } else if (confirmationData.slotId) {
        setUpdatingSlot(confirmationData.slotId);
        const response = await updateActivitySlotStatus(
          confirmationData.slotId,
          confirmationData.status
        );

        if (response.success) {
          await loadData(); // Reload data to reflect changes
        } else {
          alert('✗ Failed to update slot status. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('✗ An error occurred while updating the status');
    } finally {
      setShowConfirmation(false);
      setConfirmationData(null);
      setUpdatingSlot(null);
      setBulkUpdating(false);
    }
  };

  const cancelStatusUpdate = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  // Batch pills click handler
  const handleBatchClick = (batchId: number, batchName: string) => {
    if (!data) return;

    // Filter all slots that contain this batch
    const batchSlots = data.slots.filter((slot) =>
      slot.batches?.some((batch) => batch.batchId === batchId)
    );

    setSelectedBatchData({
      batchId,
      batchName,
      slots: batchSlots,
    });
    setShowBatchSlotsModal(true);
  };

  const closeBatchSlotsModal = () => {
    setShowBatchSlotsModal(false);
    setSelectedBatchData(null);
  };

  const handleShowStudents = (slot: ActivitySlot) => {
    setSelectedSlotForStudents({
      id: slot.id,
      name: slot.name,
    });
    setStudentDialogOpen(true);
  };

  const handleCloseStudentDialog = () => {
    setStudentDialogOpen(false);
    setSelectedSlotForStudents(null);
  };

  const formatTime = (time?: string): string => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'No Date';
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'Scheduled':
        return 'status-scheduled';
      case 'Conducted':
        return 'status-conducted';
      case 'Cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  const getExportData = (slots?: ActivitySlot[], groupName?: string) => {
    if (!data) return { headers: [], rows: [], groupName: '' };

    const slotsToExport = slots || data.slots;

    const headers = [
      'Date',
      'Start Time',
      'End Time',
      'Topic',
      'Instructor',
      'Timetable',
      'Batches',
      'Room',
      'Shift',
      'Centre',
      'Status',
    ];

    const rows = slotsToExport.map((slot) => [
      slot.date || '',
      slot.startTime || '',
      slot.endTime || '',
      slot.topicName || '',
      slot.instructorName || '',
      slot.timetableName || '',
      slot.batches?.map((b) => b.batchName).join('; ') || '',
      slot.roomName || '',
      slot.shiftName || '',
      slot.centreName || '',
      slot.status || '',
    ]);

    return { headers, rows, groupName: groupName || 'All Slots' };
  };

  const getGroupedExportData = () => {
    if (!data) return { sheets: [] };

    const sheets: Array<{ name: string; headers: string[]; rows: (string | number)[][] }> = [];

    switch (viewMode) {
      case 'by-instructor':
        data.groupedByInstructor.forEach((group) => {
          const { headers, rows } = getExportData(group.slots, group.name);
          sheets.push({ name: group.name.substring(0, 30), headers, rows });
        });
        break;
      case 'by-room':
        data.groupedByRoom.forEach((group) => {
          const { headers, rows } = getExportData(group.slots, group.name);
          sheets.push({ name: group.name.substring(0, 30), headers, rows });
        });
        break;
      case 'by-centre':
        data.groupedByCentre.forEach((group) => {
          const { headers, rows } = getExportData(group.slots, group.name);
          sheets.push({ name: group.name.substring(0, 30), headers, rows });
        });
        break;
      case 'by-shift':
        data.groupedByShift.forEach((group) => {
          const { headers, rows } = getExportData(group.slots, group.name);
          sheets.push({ name: group.name.substring(0, 30), headers, rows });
        });
        break;
      case 'by-date':
        data.groupedByDate.forEach((group) => {
          const { headers, rows } = getExportData(group.slots, formatDate(group.date));
          sheets.push({ name: formatDate(group.date).substring(0, 30), headers, rows });
        });
        break;
      default:
        const { headers, rows } = getExportData();
        sheets.push({ name: 'All Slots', headers, rows });
    }

    return { sheets };
  };

  const exportToCSV = () => {
    if (!data) return;

    const { sheets } = getGroupedExportData();

    if (sheets.length === 1) {
      // Single group export
      const { headers, rows } = sheets[0];
      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell) => `"${cell}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `birds-view-${sheets[0].name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      // Multiple groups - create separate CSV for each
      sheets.forEach((sheet) => {
        const csvContent = [sheet.headers, ...sheet.rows]
          .map((row) => row.map((cell) => `"${cell}"`).join(','))
          .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `birds-view-${sheet.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      });
    }

    setShowExportMenu(false);
  };

  const exportToExcel = () => {
    if (!data) return;

    const { sheets } = getGroupedExportData();

    // Create workbook with multiple sheets
    const wb = XLSX.utils.book_new();

    // Column widths configuration
    const colWidths = [
      { wch: 12 }, // Date
      { wch: 10 }, // Start Time
      { wch: 10 }, // End Time
      { wch: 30 }, // Topic
      { wch: 20 }, // Instructor
      { wch: 20 }, // Timetable
      { wch: 25 }, // Batches
      { wch: 15 }, // Room
      { wch: 15 }, // Shift
      { wch: 15 }, // Centre
      { wch: 12 }, // Status
    ];

    // Add each group as a separate sheet
    sheets.forEach((sheet) => {
      const sheetData = XLSX.utils.aoa_to_sheet([sheet.headers, ...sheet.rows]);
      sheetData['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(wb, sheetData, sheet.name);
    });

    // Add Analytics Summary sheet
    const analytics = data.analytics;
    const analyticsData = [
      ['Birds View Analytics'],
      [''],
      ['View Mode', viewMode.replace(/-/g, ' ').toUpperCase()],
      ['Total Slots', analytics.totalSlots],
      ['Total Teaching Hours', analytics.totalHours],
      ['Number of Instructors', analytics.totalInstructors],
      ['Number of Rooms', analytics.totalRooms],
      ['Number of Centres', analytics.totalCentres],
      ['Number of Shifts', analytics.totalShifts],
      ['Number of Batches', analytics.totalBatches],
      ['Number of Timetables', analytics.totalTimetables],
      [''],
      ['Status Breakdown'],
      ['Scheduled Sessions', analytics.statusCounts.scheduled],
      ['Conducted Sessions', analytics.statusCounts.conducted],
      ['Cancelled Sessions', analytics.statusCounts.cancelled],
    ];
    const analyticsSheet = XLSX.utils.aoa_to_sheet(analyticsData);
    analyticsSheet['!cols'] = [{ wch: 25 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, analyticsSheet, 'Analytics');

    // Export
    const fileName = viewMode === 'all'
      ? `birds-view-all-${new Date().toISOString().split('T')[0]}.xlsx`
      : `birds-view-${viewMode}-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    setShowExportMenu(false);
  };

  const exportToPDF = () => {
    if (!data) return;

    const { sheets } = getGroupedExportData();
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Add title
    doc.setFontSize(18);
    const viewTitle = viewMode === 'all'
      ? 'Birds View - All Activity Slots'
      : `Birds View - ${viewMode.replace(/-/g, ' ').toUpperCase()}`;
    doc.text(viewTitle, 14, 15);

    // Add analytics summary
    doc.setFontSize(10);
    const analytics = data.analytics;
    doc.text(`Total Slots: ${analytics.totalSlots} | Teaching Hours: ${analytics.totalHours}h | Instructors: ${analytics.totalInstructors} | Rooms: ${analytics.totalRooms}`, 14, 25);
    doc.text(`Status: ${analytics.statusCounts.scheduled} Scheduled, ${analytics.statusCounts.conducted} Conducted, ${analytics.statusCounts.cancelled} Cancelled`, 14, 30);

    let currentY = 35;

    // Add each group's table
    sheets.forEach((sheet, index) => {
      // Add group title if there are multiple groups
      if (sheets.length > 1) {
        if (index > 0) {
          doc.addPage();
          currentY = 15;
        }
        doc.setFontSize(14);
        doc.text(sheet.name, 14, currentY);
        currentY += 8;
      }

      // Add table for this group
      autoTable(doc, {
        head: [sheet.headers],
        body: sheet.rows,
        startY: currentY,
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
        },
        headStyles: {
          fillColor: [102, 126, 234], // Purple color matching theme
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        columnStyles: {
          0: { cellWidth: 22 }, // Date
          1: { cellWidth: 18 }, // Start Time
          2: { cellWidth: 18 }, // End Time
          3: { cellWidth: 45 }, // Topic
          4: { cellWidth: 30 }, // Instructor
          5: { cellWidth: 30 }, // Timetable
          6: { cellWidth: 35 }, // Batches
          7: { cellWidth: 22 }, // Room
          8: { cellWidth: 22 }, // Shift
          9: { cellWidth: 22 }, // Centre
          10: { cellWidth: 18 }, // Status
        },
      });

      currentY = (doc as any).lastAutoTable.finalY + 10;
    });

    // Add footer with timestamp
    const pageCount = doc.getNumberOfPages();
    doc.setFontSize(8);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} | Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    // Save
    const fileName = viewMode === 'all'
      ? `birds-view-all-${new Date().toISOString().split('T')[0]}.pdf`
      : `birds-view-${viewMode}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    setShowExportMenu(false);
  };

  // Export single group
  const exportSingleGroup = (slots: ActivitySlot[], groupName: string, format: 'csv' | 'excel' | 'pdf') => {
    const { headers, rows } = getExportData(slots, groupName);

    if (format === 'csv') {
      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell) => `"${cell}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${groupName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'excel') {
      const wb = XLSX.utils.book_new();
      const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      sheet['!cols'] = [
        { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 30 }, { wch: 20 },
        { wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(wb, sheet, groupName.substring(0, 30));
      XLSX.writeFile(wb, `${groupName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`);
    } else if (format === 'pdf') {
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(18);
      doc.text(groupName, 14, 15);

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 25,
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [102, 126, 234], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
          0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 18 },
          3: { cellWidth: 45 }, 4: { cellWidth: 30 }, 5: { cellWidth: 30 },
          6: { cellWidth: 35 }, 7: { cellWidth: 22 }, 8: { cellWidth: 22 },
          9: { cellWidth: 22 }, 10: { cellWidth: 18 },
        },
      });

      const pageCount = doc.getNumberOfPages();
      doc.setFontSize(8);
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.text(
          `Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      doc.save(`${groupName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
    }
  };

  const renderSlotCard = (slot: ActivitySlot) => {
    const isSelected = selectedSlots.has(slot.id);
    const isUpdating = updatingSlot === slot.id;
    const currentStatus = slot.status as string;

    return (
      <div
        key={slot.id}
        className={`bev-slot-card ${isSelected ? 'bev-slot-selected' : ''} ${isUpdating ? 'bev-slot-updating' : ''}`}
      >
        <div className="bev-slot-header">
          {bulkMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSlotSelection(slot.id)}
              className="bev-slot-checkbox"
            />
          )}
          <div className="bev-slot-date-time">
            <div className="bev-slot-date">
              <span className="date-icon">📅</span>
              <span>{formatDate(slot.date)}</span>
            </div>
            <div className="bev-slot-time">
              <span className="time-icon">🕐</span>
              <span>{formatTime(slot.startTime)} - {formatTime(slot.endTime)}</span>
            </div>
          </div>
          <span className={`bev-status-badge ${getStatusColor(currentStatus)}`}>
            {slot.status}
          </span>
          {!bulkMode && (
            <div className="bev-slot-actions">
              <button
                className="action-menu-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  // Set flag to ignore the next document click
                  ignoreNextClickRef.current = true;
                  setOpenActionMenu(openActionMenu === slot.id ? null : slot.id);
                }}
                disabled={isUpdating}
              >
                ⋮
              </button>
              {openActionMenu === slot.id && (
                <div className="action-menu">
                  {currentStatus === 'Scheduled' && (
                    <>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleQuickStatusUpdate(slot.id, 'Conducted');
                      }}>
                        ✓ Mark as Conducted
                      </button>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleQuickStatusUpdate(slot.id, 'Cancelled');
                      }}>
                        ✗ Mark as Cancelled
                      </button>
                    </>
                  )}
                  {currentStatus === 'Conducted' && (
                    <button onClick={(e) => {
                      e.stopPropagation();
                      handleQuickStatusUpdate(slot.id, 'Cancelled');
                    }}>
                      ✗ Mark as Cancelled
                    </button>
                  )}
                  {currentStatus === 'Cancelled' && (
                    <button onClick={(e) => {
                      e.stopPropagation();
                      handleQuickStatusUpdate(slot.id, 'Scheduled');
                    }}>
                      ↻ Reactivate (Scheduled)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bev-slot-body">
          <h4 className="bev-slot-topic">
            {slot.topicName || 'No Topic'}
            {slot.timetableEffectiveFrom && (
              <span className="bev-slot-start-date">
                {' '}(Started: {formatDate(slot.timetableEffectiveFrom)})
              </span>
            )}
          </h4>

          <div className="bev-slot-details">
            {slot.instructorName && (
              <div className="bev-detail-item bev-instructor-item">
                {slot.instructorPhoto ? (
                  <img
                    src={slot.instructorPhoto}
                    alt={slot.instructorName}
                    className="instructor-avatar"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <span className="instructor-avatar-fallback" style={{ display: slot.instructorPhoto ? 'none' : 'flex' }}>
                  👨‍🏫
                </span>
                <span>{slot.instructorName}</span>
              </div>
            )}
            {slot.roomName && (
              <div className="bev-detail-item">
                <span className="detail-icon">🏫</span>
                <span>{slot.roomName}</span>
              </div>
            )}
            {slot.shiftName && (
              <div className="bev-detail-item">
                <span className="detail-icon">⏰</span>
                <span>{slot.shiftName}</span>
              </div>
            )}
            {slot.centreName && (
              <div className="bev-detail-item">
                <span className="detail-icon">📍</span>
                <span>{slot.centreName}</span>
              </div>
            )}
          </div>

          {typeof slot.studentCount !== 'undefined' && (
            <div className="bev-student-count" onClick={(e) => {
              e.stopPropagation();
              handleShowStudents(slot);
            }} style={{
              cursor: 'pointer',
              padding: '6px 12px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#1976d2',
              border: '1px solid #bbdefb',
              transition: 'all 0.2s ease',
            }}>
              <span>👥</span>
              <span>{slot.studentCount} student{slot.studentCount !== 1 ? 's' : ''}</span>
            </div>
          )}

          {slot.batches && slot.batches.length > 0 && (
            <div className="bev-batches">
              {slot.batches.map((batch) => (
                <span
                  key={batch.id}
                  className="bev-batch-chip"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBatchClick(batch.batchId, batch.batchName);
                  }}
                  title={`Click to see all ${batch.batchName} sessions`}
                >
                  {batch.batchName}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Activity Slot ID */}
        <div className="bev-slot-id">
          ID: {slot.id}
        </div>
      </div>
    );
  };

  const renderAnalytics = () => {
    if (!data) return null;

    const { analytics } = data;
    const hasData = analytics.totalSlots > 0;

    return (
      <div className="bev-analytics-dashboard">
        <h3 className="bev-section-title">📊 System Overview</h3>
        {!hasData && (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            background: 'var(--surface-page)',
            borderRadius: '12px',
            border: '2px dashed #cbd5e0',
            marginBottom: '1.5rem',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
            <h4 style={{ color: 'var(--text-body)', marginBottom: '0.5rem' }}>No Data Available</h4>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
              No activity slots found for the selected date range and filters.
            </p>
            <p style={{ color: 'var(--text-faint)', fontSize: '0.875rem' }}>
              Try adjusting your date range or clearing filters to see more results.
            </p>
          </div>
        )}
        <div className="bev-analytics-grid">
          <div className="bev-analytics-card analytics-primary">
            <div className="analytics-icon">📚</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalSlots}</div>
              <div className="analytics-label">Total Slots</div>
            </div>
          </div>

          <div className="bev-analytics-card analytics-secondary">
            <div className="analytics-icon">⏱️</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalHours}h</div>
              <div className="analytics-label">Teaching Hours</div>
            </div>
          </div>

          <div className="bev-analytics-card analytics-accent">
            <div className="analytics-icon">👨‍🏫</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalInstructors}</div>
              <div className="analytics-label">Instructors</div>
            </div>
          </div>

          <div className="bev-analytics-card analytics-info">
            <div className="analytics-icon">🏫</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalRooms}</div>
              <div className="analytics-label">Rooms</div>
            </div>
          </div>

          <div className="bev-analytics-card analytics-warning">
            <div className="analytics-icon">📍</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalCentres}</div>
              <div className="analytics-label">Centres</div>
            </div>
          </div>

          <div className="bev-analytics-card analytics-success">
            <div className="analytics-icon">⏰</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalShifts}</div>
              <div className="analytics-label">Shifts</div>
            </div>
          </div>

          <div className="bev-analytics-card analytics-purple">
            <div className="analytics-icon">👥</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalBatches}</div>
              <div className="analytics-label">Batches</div>
            </div>
          </div>

          <div className="bev-analytics-card analytics-teal">
            <div className="analytics-icon">📋</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalTimetables}</div>
              <div className="analytics-label">Timetables</div>
            </div>
          </div>

          {/* Capacity-based metrics */}
          {analytics.capacityMetrics && (
            <>
              <div
                className="bev-analytics-card analytics-purple"
                style={{ borderLeftColor: '#9f7aea', cursor: 'pointer' }}
                onClick={() => setRoomUtilizationModalOpen(true)}
                title="Click to view detailed room utilization"
              >
                <div className="analytics-icon">🏢</div>
                <div className="analytics-content">
                  <div className="analytics-value">{analytics.capacityMetrics.rooms.utilizationRate}%</div>
                  <div className="analytics-label">Room Utilization</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {analytics.capacityMetrics.rooms.totalScheduledHours}h / {analytics.capacityMetrics.rooms.totalAvailableHours}h
                  </div>
                </div>
              </div>

              <div
                className="bev-analytics-card analytics-accent"
                style={{ borderLeftColor: '#ed64a6', cursor: 'pointer' }}
                onClick={() => setInstructorUtilizationModalOpen(true)}
                title="Click to view detailed instructor utilization"
              >
                <div className="analytics-icon">👔</div>
                <div className="analytics-content">
                  <div className="analytics-value">{analytics.capacityMetrics.instructors.utilizationRate}%</div>
                  <div className="analytics-label">Instructor Load</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {analytics.capacityMetrics.instructors.totalTeachingHours}h / {analytics.capacityMetrics.instructors.totalAvailableHours}h
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="bev-status-overview">
          <h4>Status Distribution</h4>
          <div className="status-bars">
            <div className="status-bar-item">
              <div className="status-bar-label">
                <span className="status-dot status-scheduled"></span>
                <span>Scheduled ({analytics.statusCounts.scheduled})</span>
              </div>
              <div className="status-bar-track">
                <div
                  className="status-bar-fill status-scheduled-fill"
                  style={{
                    width: `${analytics.totalSlots > 0 ? (analytics.statusCounts.scheduled / analytics.totalSlots) * 100 : 0}%`,
                  }}
                ></div>
              </div>
            </div>

            <div className="status-bar-item">
              <div className="status-bar-label">
                <span className="status-dot status-conducted"></span>
                <span>Conducted ({analytics.statusCounts.conducted})</span>
              </div>
              <div className="status-bar-track">
                <div
                  className="status-bar-fill status-conducted-fill"
                  style={{
                    width: `${analytics.totalSlots > 0 ? (analytics.statusCounts.conducted / analytics.totalSlots) * 100 : 0}%`,
                  }}
                ></div>
              </div>
            </div>

            <div className="status-bar-item">
              <div className="status-bar-label">
                <span className="status-dot status-cancelled"></span>
                <span>Cancelled ({analytics.statusCounts.cancelled})</span>
              </div>
              <div className="status-bar-track">
                <div
                  className="status-bar-fill status-cancelled-fill"
                  style={{
                    width: `${analytics.totalSlots > 0 ? (analytics.statusCounts.cancelled / analytics.totalSlots) * 100 : 0}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className={`bev-filters-panel ${showFilters ? 'bev-filters-visible' : ''}`}>
        <div className="bev-filters-grid">
          <div className="bev-filter-item">
            <label htmlFor="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="bev-filter-input"
            />
          </div>

          <div className="bev-filter-item">
            <label htmlFor="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="bev-filter-input"
            />
          </div>

          <div className="bev-filter-item">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="bev-filter-select"
            >
              <option value="">All Statuses</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Conducted">Conducted</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="bev-filter-item">
            <label htmlFor="searchSlotId">Search by Slot ID</label>
            <input
              type="text"
              id="searchSlotId"
              value={searchSlotId}
              onChange={(e) => setSearchSlotId(e.target.value)}
              placeholder="Enter slot ID..."
              className="bev-filter-input"
            />
          </div>

          <div className="bev-filter-actions">
            <button onClick={() => {
              clearFilters();
              setSearchSlotId('');
            }} className="bev-btn-clear-filters">
              Clear All Filters
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Filter slots by search ID
  const filterSlotsBySearch = (slots: ActivitySlot[]): ActivitySlot[] => {
    if (!searchSlotId || searchSlotId.trim() === '') {
      return slots;
    }
    return slots.filter(slot => slot.id.toString().includes(searchSlotId.trim()));
  };

  const renderContent = () => {
    if (!data || data.slots.length === 0) {
      return (
        <div className="bev-empty-state">
          <div className="empty-icon">📅</div>
          <h2>No Activity Slots Found</h2>
          <p>Try adjusting your filters to see more results.</p>
        </div>
      );
    }

    // Apply search filter to all slots
    const filteredSlots = filterSlotsBySearch(data.slots);

    if (filteredSlots.length === 0) {
      return (
        <div className="bev-empty-state">
          <div className="empty-icon">🔍</div>
          <h2>No Matching Slots Found</h2>
          <p>No slots match the ID "{searchSlotId}". Try a different search term.</p>
        </div>
      );
    }

    switch (viewMode) {
      case 'by-instructor':
        return (
          <div className="bev-grouped-view">
            <h3 className="bev-section-title">👨‍🏫 By Instructor ({data.groupedByInstructor.length})</h3>
            {data.groupedByInstructor.map((group) => {
              const groupFilteredSlots = filterSlotsBySearch(group.slots);
              if (groupFilteredSlots.length === 0) return null;
              return (
                <div key={group.id} className="bev-group-section">
                  <div className="bev-group-header">
                    <div className="bev-group-title-with-photo">
                      {group.slots && group.slots[0]?.instructorPhoto ? (
                        <img
                          src={group.slots[0].instructorPhoto}
                          alt={group.name}
                          className="group-instructor-avatar"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : null}
                      <h4>{group.name}</h4>
                    </div>
                    <div className="bev-group-header-actions">
                      <span className="bev-group-count">{groupFilteredSlots.length} slots</span>
                      <div className="bev-group-export-buttons">
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'csv')}
                          className="bev-export-btn"
                          title="Export to CSV"
                        >
                          📄 CSV
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'excel')}
                          className="bev-export-btn"
                          title="Export to Excel"
                        >
                          📊 Excel
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'pdf')}
                          className="bev-export-btn"
                          title="Export to PDF"
                        >
                          📑 PDF
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bev-slots-grid">
                    {groupFilteredSlots.map((slot) => renderSlotCard(slot))}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'by-room':
        return (
          <div className="bev-grouped-view">
            <h3 className="bev-section-title">🏫 By Room ({data.groupedByRoom.length})</h3>
            {data.groupedByRoom.map((group) => {
              const groupFilteredSlots = filterSlotsBySearch(group.slots);
              if (groupFilteredSlots.length === 0) return null;
              return (
                <div key={group.id} className="bev-group-section">
                  <div className="bev-group-header">
                    <h4>{group.name}</h4>
                    <div className="bev-group-header-actions">
                      <span className="bev-group-count">{groupFilteredSlots.length} slots</span>
                      <div className="bev-group-export-buttons">
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'csv')}
                          className="bev-export-btn"
                          title="Export to CSV"
                        >
                          📄 CSV
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'excel')}
                          className="bev-export-btn"
                          title="Export to Excel"
                        >
                          📊 Excel
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'pdf')}
                          className="bev-export-btn"
                          title="Export to PDF"
                        >
                          📑 PDF
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bev-slots-grid">
                    {groupFilteredSlots.map((slot) => renderSlotCard(slot))}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'by-centre':
        return (
          <div className="bev-grouped-view">
            <h3 className="bev-section-title">📍 By Centre ({data.groupedByCentre.length})</h3>
            {data.groupedByCentre.map((group) => {
              const groupFilteredSlots = filterSlotsBySearch(group.slots);
              if (groupFilteredSlots.length === 0) return null;
              return (
                <div key={group.id} className="bev-group-section">
                  <div className="bev-group-header">
                    <h4>{group.name}</h4>
                    <div className="bev-group-header-actions">
                      <span className="bev-group-count">{groupFilteredSlots.length} slots</span>
                      <div className="bev-group-export-buttons">
                        <button
                          onClick={() => openTVDisplay(group.id, group.name)}
                          className="bev-tv-display-btn"
                          title="Open TV Display Board (opens in new tab)"
                        >
                          📺 TV Display
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'csv')}
                          className="bev-export-btn"
                          title="Export to CSV"
                        >
                          📄 CSV
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'excel')}
                          className="bev-export-btn"
                          title="Export to Excel"
                        >
                          📊 Excel
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'pdf')}
                          className="bev-export-btn"
                          title="Export to PDF"
                        >
                          📑 PDF
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bev-slots-grid">
                    {groupFilteredSlots.map((slot) => renderSlotCard(slot))}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'by-shift':
        return (
          <div className="bev-grouped-view">
            <h3 className="bev-section-title">⏰ By Shift ({data.groupedByShift.length})</h3>
            {data.groupedByShift.map((group) => {
              const groupFilteredSlots = filterSlotsBySearch(group.slots);
              if (groupFilteredSlots.length === 0) return null;
              return (
                <div key={group.id} className="bev-group-section">
                  <div className="bev-group-header">
                    <h4>{group.name}</h4>
                    <div className="bev-group-header-actions">
                      <span className="bev-group-count">{groupFilteredSlots.length} slots</span>
                      <div className="bev-group-export-buttons">
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'csv')}
                          className="bev-export-btn"
                          title="Export to CSV"
                        >
                          📄 CSV
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'excel')}
                          className="bev-export-btn"
                          title="Export to Excel"
                        >
                          📊 Excel
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, group.name, 'pdf')}
                          className="bev-export-btn"
                          title="Export to PDF"
                        >
                          📑 PDF
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bev-slots-grid">
                    {groupFilteredSlots.map((slot) => renderSlotCard(slot))}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'by-date':
        return (
          <div className="bev-grouped-view">
            <h3 className="bev-section-title">📅 By Date ({data.groupedByDate.length} days)</h3>
            {data.groupedByDate.map((group) => {
              const groupFilteredSlots = filterSlotsBySearch(group.slots);
              if (groupFilteredSlots.length === 0) return null;
              return (
                <div key={group.date} className="bev-group-section">
                  <div className="bev-group-header">
                    <h4>{formatDate(group.date)}</h4>
                    <div className="bev-group-header-actions">
                      <span className="bev-group-count">{groupFilteredSlots.length} slots</span>
                      <div className="bev-group-export-buttons">
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, formatDate(group.date), 'csv')}
                          className="bev-export-btn"
                          title="Export to CSV"
                        >
                          📄 CSV
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, formatDate(group.date), 'excel')}
                          className="bev-export-btn"
                          title="Export to Excel"
                        >
                          📊 Excel
                        </button>
                        <button
                          onClick={() => exportSingleGroup(groupFilteredSlots, formatDate(group.date), 'pdf')}
                          className="bev-export-btn"
                          title="Export to PDF"
                        >
                          📑 PDF
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bev-slots-grid">
                    {groupFilteredSlots.map((slot) => renderSlotCard(slot))}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'all':
      default:
        return (
          <div className="bev-all-view">
            <h3 className="bev-section-title">📋 All Slots ({filteredSlots.length})</h3>
            <div className="bev-slots-grid">
              {filteredSlots.map((slot) => renderSlotCard(slot))}
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="bev-loading">
        <div className="loading-spinner"></div>
        <p>Loading birds view...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bev-error">
        <div className="error-icon">⚠️</div>
        <p className="error-message">{error}</p>
        <button onClick={loadData} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="birds-eye-view-container">
      <div className="bev-header">
        <div className="bev-header-content">
          <h1 className="bev-title">
            <span className="bev-icon">🦅</span>
            Birds View
          </h1>
          <div className="bev-header-actions">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`bev-btn-toggle-filters ${showFilters ? 'active' : ''}`}
            >
              <span className="filter-icon">🔍</span>
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            <button
              onClick={toggleBulkMode}
              className={`bev-btn-bulk-mode ${bulkMode ? 'active' : ''}`}
            >
              <span className="bulk-icon">☑</span>
              {bulkMode ? 'Exit Bulk' : 'Bulk Update'}
            </button>
            <div className="export-dropdown-wrapper">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn-export"
              >
                <span className="export-icon">📥</span>
                Export
                <span className="dropdown-arrow">{showExportMenu ? '▲' : '▼'}</span>
              </button>
              {showExportMenu && (
                <div className="export-dropdown-menu">
                  <button onClick={exportToCSV} className="export-option">
                    <span className="option-icon">📄</span>
                    <div className="option-content">
                      <span className="option-title">CSV</span>
                      <span className="option-desc">Comma-separated values</span>
                    </div>
                  </button>
                  <button onClick={exportToExcel} className="export-option">
                    <span className="option-icon">📊</span>
                    <div className="option-content">
                      <span className="option-title">Excel</span>
                      <span className="option-desc">Multi-sheet workbook</span>
                    </div>
                  </button>
                  <button onClick={exportToPDF} className="export-option">
                    <span className="option-icon">📑</span>
                    <div className="option-content">
                      <span className="option-title">PDF</span>
                      <span className="option-desc">Printable document</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <p className="bev-subtitle">
          Complete overview of all activity slots across the entire system
        </p>
      </div>

      {renderFilters()}
      {renderAnalytics()}

      <div className="bev-view-selector">
        <button
          onClick={() => setViewMode('all')}
          className={`bev-view-btn ${viewMode === 'all' ? 'active' : ''}`}
        >
          <span className="view-icon">📋</span>
          All Slots
        </button>
        <button
          onClick={() => setViewMode('by-instructor')}
          className={`bev-view-btn ${viewMode === 'by-instructor' ? 'active' : ''}`}
        >
          <span className="view-icon">👨‍🏫</span>
          By Instructor
        </button>
        <button
          onClick={() => setViewMode('by-room')}
          className={`bev-view-btn ${viewMode === 'by-room' ? 'active' : ''}`}
        >
          <span className="view-icon">🏫</span>
          By Room
        </button>
        <button
          onClick={() => setViewMode('by-centre')}
          className={`bev-view-btn ${viewMode === 'by-centre' ? 'active' : ''}`}
        >
          <span className="view-icon">📍</span>
          By Centre
        </button>
        <button
          onClick={() => setViewMode('by-shift')}
          className={`bev-view-btn ${viewMode === 'by-shift' ? 'active' : ''}`}
        >
          <span className="view-icon">⏰</span>
          By Shift
        </button>
        <button
          onClick={() => setViewMode('by-date')}
          className={`bev-view-btn ${viewMode === 'by-date' ? 'active' : ''}`}
        >
          <span className="view-icon">📅</span>
          By Date
        </button>
      </div>

      <div className="bev-content">{renderContent()}</div>

      {/* Bulk Action Bar */}
      {bulkMode && selectedSlots.size > 0 && (
        <div className="bev-bulk-action-bar">
          <div className="bulk-action-content">
            <span className="bulk-selected-count">
              {selectedSlots.size} slot{selectedSlots.size !== 1 ? 's' : ''} selected
            </span>
            <div className="bulk-action-controls">
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value as 'Scheduled' | 'Conducted' | 'Cancelled')}
                className="bulk-status-select"
              >
                <option value="Conducted">Mark as Conducted</option>
                <option value="Cancelled">Mark as Cancelled</option>
                <option value="Scheduled">Mark as Scheduled</option>
              </select>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={bulkUpdating}
                className="bulk-apply-btn"
              >
                {bulkUpdating ? 'Updating...' : 'Apply to Selected'}
              </button>
              <button onClick={deselectAllSlots} className="bulk-cancel-btn">
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && confirmationData && (
        <div className="bev-confirmation-overlay" onClick={cancelStatusUpdate}>
          <div className="bev-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Status Update</h3>
            <p>
              {confirmationData.isBulk
                ? `Are you sure you want to mark ${confirmationData.slotIds?.length} slot(s) as ${confirmationData.status}?`
                : `Are you sure you want to mark this slot as ${confirmationData.status}?`}
            </p>
            <div className="confirmation-actions">
              <button onClick={confirmStatusUpdate} className="btn-confirm">
                Yes, Update
              </button>
              <button onClick={cancelStatusUpdate} className="btn-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Slots Modal */}
      {showBatchSlotsModal && selectedBatchData && (
        <div className="bev-confirmation-overlay" onClick={closeBatchSlotsModal}>
          <div
            className="bev-batch-slots-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="batch-modal-header">
              <h3>
                📚 {selectedBatchData.batchName}
              </h3>
              <p className="batch-modal-subtitle">
                {selectedBatchData.slots.length} session{selectedBatchData.slots.length !== 1 ? 's' : ''} found
              </p>
              <button
                className="batch-modal-close"
                onClick={closeBatchSlotsModal}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="batch-modal-content">
              {selectedBatchData.slots.length === 0 ? (
                <div className="batch-modal-empty">
                  <p>No sessions found for this batch</p>
                </div>
              ) : (
                <>
                  {/* Group slots by date */}
                  {(() => {
                    const groupedByDate = selectedBatchData.slots.reduce((acc, slot) => {
                      const date = slot.date || 'No Date';
                      if (!acc[date]) {
                        acc[date] = [];
                      }
                      acc[date].push(slot);
                      return acc;
                    }, {} as Record<string, ActivitySlot[]>);

                    // Sort dates
                    const sortedDates = Object.keys(groupedByDate).sort();

                    return sortedDates.map((date) => (
                      <div key={date} className="batch-modal-date-group">
                        <h4 className="batch-modal-date-header">{formatDate(date)}</h4>
                        <div className="batch-modal-slots">
                          {groupedByDate[date]
                            .sort((a, b) => {
                              const timeA = a.startTime || '00:00';
                              const timeB = b.startTime || '00:00';
                              return timeA.localeCompare(timeB);
                            })
                            .map((slot) => (
                              <div key={slot.id} className="batch-modal-slot-card">
                                <div className="batch-slot-time">
                                  <span className="time-icon">🕐</span>
                                  <span className="time-text">
                                    {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                  </span>
                                  <span className={`batch-slot-status ${getStatusColor(slot.status as string)}`}>
                                    {slot.status}
                                  </span>
                                </div>
                                <div className="batch-slot-topic">
                                  {slot.topicName || 'No Topic'}
                                </div>
                                <div className="batch-slot-details">
                                  {slot.instructorName && (
                                    <div className="batch-slot-detail">
                                      <span className="detail-icon">👨‍🏫</span>
                                      <span>{slot.instructorName}</span>
                                    </div>
                                  )}
                                  {slot.roomName && (
                                    <div className="batch-slot-detail">
                                      <span className="detail-icon">🏫</span>
                                      <span>{slot.roomName}</span>
                                    </div>
                                  )}
                                  {slot.shiftName && (
                                    <div className="batch-slot-detail">
                                      <span className="detail-icon">⏰</span>
                                      <span>{slot.shiftName}</span>
                                    </div>
                                  )}
                                </div>
                                {slot.timetableName && (
                                  <div className="batch-slot-timetable">
                                    📋 {slot.timetableName}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    ));
                  })()}
                </>
              )}
            </div>
            <div className="batch-modal-footer">
              <button onClick={closeBatchSlotsModal} className="btn-close-batch-modal">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student List Dialog */}
      <StudentListDialog
        open={studentDialogOpen}
        onClose={handleCloseStudentDialog}
        activitySlotId={selectedSlotForStudents?.id || null}
        activitySlotName={selectedSlotForStudents?.name}
      />

      {/* Room Utilization Modal */}
      {data?.analytics.capacityMetrics && (
        <AssetUtilizationModal
          open={roomUtilizationModalOpen}
          onClose={() => setRoomUtilizationModalOpen(false)}
          title="Room Utilization Details"
          assetType="room"
          data={data.analytics.capacityMetrics.rooms.details}
        />
      )}

      {/* Instructor Utilization Modal */}
      {data?.analytics.capacityMetrics && (
        <AssetUtilizationModal
          open={instructorUtilizationModalOpen}
          onClose={() => setInstructorUtilizationModalOpen(false)}
          title="Instructor Utilization Details"
          assetType="instructor"
          data={data.analytics.capacityMetrics.instructors.details}
        />
      )}
    </div>
  );
};

export default BirdsEyeView;
